import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentOrganization } from "@/hooks/organization";
import { useMonitoring } from "@/hooks/useMonitoring";
import { toSentryContext } from "@/application/services/authUtils";

/**
 * Keeps the Sentry user-context payload aligned with the currently
 * authenticated user *and* their active organization.
 *
 * Login use-cases seed the context with `null` organization (the org store
 * has not resolved yet at that point). This hook re-applies the canonical
 * payload whenever the user or the selected organization changes — including
 * org switches — so every Sentry event captured after that point carries the
 * right `organization_uuid` / `organization_name` / `role`.
 *
 * Contract: see `docs/standards/infra/observability/strategy-and-sentry.md`
 * §2.10.
 */
export function useSentryUserContext(): void {
  const { user, isAuthenticated } = useAuth();
  const organization = useCurrentOrganization();
  const monitoring = useMonitoring();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }
    monitoring.setUserContext(toSentryContext(user, organization));
  }, [
    isAuthenticated,
    user,
    organization?.organization_uuid,
    organization?.organization_name,
    organization?.user_role,
    monitoring,
  ]);
}
